import React from 'react';
import { LineChart } from '@mui/x-charts';
import { Box, Typography, Paper } from '@mui/material';
import dayjs from 'dayjs';
import styles from './styles.module.css';

// ─── Palette ──────────────────────────────────────────────────────
const chartPalette = {
  blue: '#5A80FB',
  bluePale: '#E8EDFF',
  blueBg: '#F5F7FF',
  pink: '#ED3680',
  pinkPale: '#FFDFEC',
  pinkBg: '#FFF0F6',
  navy: '#1A2559',
  textDark: '#2D3748',
  textMedium: '#4A5568',
  textLight: '#718096',
  textMuted: '#A0AEC0',
  border: '#E8ECF4',
  white: '#FFFFFF',
  gridLine: '#F0F3F9',
};

// ─── Severity helpers ─────────────────────────────────────────────
const getSeverityBadgeStyle = (severity) => {
  const map = {
    'Normal': { color: '#2E7D32', bg: '#E8F5E9', border: '#C8E6C9' },
    'Leve': { color: '#F9A825', bg: '#FFFDE7', border: '#FFF9C4' },
    'Moderada': { color: '#E65100', bg: '#FFF3E0', border: '#FFE0B2' },
    'Severa': { color: '#C62828', bg: '#FFEBEE', border: '#FFCDD2' },
    'Muito severa': { color: '#B71C1C', bg: '#FFCDD2', border: '#EF9A9A' },
  };
  return map[severity] || { color: chartPalette.textMedium, bg: '#F8F9FD', border: chartPalette.border };
};

const getApBpSeverity = (percentage) => {
  if (percentage >= 75.01 && percentage <= 90.00) return 'Normal';
  if ((percentage >= 71.01 && percentage <= 75.00) || (percentage >= 90.01 && percentage <= 94.00)) return 'Leve';
  if ((percentage >= 68.01 && percentage <= 71.00) || (percentage >= 94.01 && percentage <= 98.00)) return 'Moderada';
  if ((percentage >= 65.01 && percentage <= 68.00) || (percentage >= 98.01 && percentage <= 102.00)) return 'Severa';
  if (percentage <= 65.00 || percentage > 102.00) return 'Muito severa';
  return 'Desconhecido';
};

const getPlagioSeverity = (percentage) => {
  if (percentage <= 3.49) return 'Normal';
  if (percentage >= 3.5 && percentage <= 6.25) return 'Leve';
  if (percentage >= 6.26 && percentage <= 8.75) return 'Moderada';
  if (percentage >= 8.76 && percentage <= 10.99) return 'Severa';
  if (percentage >= 11.00) return 'Muito severa';
  return 'Desconhecido';
};

// ─── Zone colors (very subtle pastels) ────────────────────────────
const apBpZones = [
  { min: 0, max: 65.00, severity: "Muito severa", color: 'rgba(244, 67, 54, 0.04)' },
  { min: 65.01, max: 68.00, severity: "Severa", color: 'rgba(244, 67, 54, 0.04)' },
  { min: 68.01, max: 71.00, severity: "Moderada", color: 'rgba(255, 152, 0, 0.04)' },
  { min: 71.01, max: 75.00, severity: "Leve", color: 'rgba(255, 193, 7, 0.04)' },
  { min: 75.01, max: 90.00, severity: "Normal", color: 'rgba(76, 175, 80, 0.04)' },
  { min: 90.01, max: 94.00, severity: "Leve", color: 'rgba(255, 193, 7, 0.04)' },
  { min: 94.01, max: 98.00, severity: "Moderada", color: 'rgba(255, 152, 0, 0.04)' },
  { min: 98.01, max: 102.00, severity: "Severa", color: 'rgba(244, 67, 54, 0.04)' },
  { min: 102.01, max: 110.00, severity: "Muito severa", color: 'rgba(244, 67, 54, 0.04)' },
];

const plagiocephalyZones = [
  { min: 0, max: 3.49, severity: "Normal", color: 'rgba(76, 175, 80, 0.04)' },
  { min: 3.5, max: 6.25, severity: "Leve", color: 'rgba(255, 193, 7, 0.04)' },
  { min: 6.26, max: 8.75, severity: "Moderada", color: 'rgba(255, 152, 0, 0.04)' },
  { min: 8.76, max: 10.99, severity: "Severa", color: 'rgba(244, 67, 54, 0.04)' },
  { min: 11.00, max: 15.00, severity: "Muito severa", color: 'rgba(244, 67, 54, 0.04)' },
];

// ─── SVG Icon Components ──────────────────────────────────────────
const ChartLineIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

const BrainIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

// ─── Zone Overlay Component ───────────────────────────────────────
const ZoneOverlay = ({ zones, yMin, yMax }) => (
  <>
    {zones
      .filter(zone => zone.max >= yMin && zone.min <= yMax)
      .map((zone, index) => {
        const yStart = Math.max(zone.min, yMin);
        const yEnd = Math.min(zone.max, yMax);
        const height = yEnd - yStart;
        return (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: 0,
              bottom: `${((yStart - yMin) / (yMax - yMin)) * 100}%`,
              width: '100%',
              height: `${(height / (yMax - yMin)) * 100}%`,
              backgroundColor: zone.color,
              zIndex: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: '6px',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: chartPalette.textMedium,
                fontWeight: 500,
                fontSize: '9px',
                opacity: 0.8,
              }}
            >
              {zone.severity}
            </Typography>
          </Box>
        );
      })}
  </>
);

// ─── Severity Badge ───────────────────────────────────────────────
const SeverityBadge = ({ severity }) => {
  const sv = getSeverityBadgeStyle(severity);
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 600,
      color: sv.color,
      backgroundColor: sv.bg,
      border: `1px solid ${sv.border}`,
      whiteSpace: 'nowrap',
      lineHeight: '1.4',
    }}>
      {severity}
    </span>
  );
};

// ─── Data Point Labels Overlay ────────────────────────────────────
const DataPointLabels = ({ chartData, yMin, yMax, accentColor, chartMargin, chartHeight }) => {
  const totalPoints = chartData.length;
  if (totalPoints === 0) return null;

  // Chart uses band scale: points are centered in each band
  // Band width = usableWidth / totalPoints, point is at center of band
  const containerHeight = chartHeight;
  const plotTop = chartMargin.top;
  const plotBottom = chartMargin.bottom;
  const plotHeight = containerHeight - plotTop - plotBottom;

  return (
    <>
      {chartData.map((value, index) => {
        // X: band scale - each band is 1/N of plot width, point is at center
        const bandFraction = (index + 0.5) / totalPoints;
        const xPx = chartMargin.left + bandFraction * (400 - chartMargin.left - chartMargin.right);
        const xPercent = (xPx / 400) * 100;

        // Y: linear scale from yMin (bottom) to yMax (top)
        const yFraction = (value - yMin) / (yMax - yMin);
        const yFromTop = plotTop + plotHeight * (1 - yFraction);
        const yPercent = (yFromTop / containerHeight) * 100;

        const isLast = index === totalPoints - 1;

        return (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: `${xPercent}%`,
              top: `${yPercent}%`,
              transform: 'translate(-50%, -130%)',
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            <Typography sx={{
              fontSize: isLast ? '11px' : '9px',
              fontWeight: isLast ? 700 : 600,
              color: isLast ? accentColor : chartPalette.textMedium,
              textAlign: 'center',
              lineHeight: 1,
              textShadow: `0 0 3px ${chartPalette.white}, 0 0 6px ${chartPalette.white}`,
            }}>
              {value}%
            </Typography>
          </Box>
        );
      })}
    </>
  );
};

// ─── Chart Card Component ─────────────────────────────────────────
const ChartCard = ({
  icon,
  title,
  currentValue,
  severity,
  variation,
  variationDirection,
  accentColor,
  accentBg,
  chartData,
  xLabels,
  xSubLabels,
  zones,
  yMin,
  yMax,
}) => {
  const variationColor = variationDirection === 'up' ? chartPalette.blue : variationDirection === 'down' ? '#E65100' : chartPalette.textMuted;
  const variationArrow = variationDirection === 'up' ? '↑' : variationDirection === 'down' ? '↓' : '';

  const chartMargin = { top: 20, bottom: 30, left: 38, right: 8 };

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        borderRadius: '14px',
        border: `1px solid ${chartPalette.border}`,
        overflow: 'hidden',
        backgroundColor: chartPalette.white,
      }}
    >
      {/* ── Card Header ── */}
      <Box sx={{ padding: '16px 16px 12px 16px' }}>
        {/* Title row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box sx={{
              width: '28px', height: '28px',
              borderRadius: '8px',
              backgroundColor: accentBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: chartPalette.textMedium }}>
              {title}
            </Typography>
          </Box>

          {variation !== null && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: '8px', color: chartPalette.textMuted, fontWeight: 500, lineHeight: 1.2 }}>
                Variação desde
              </Typography>
              <Typography sx={{ fontSize: '8px', color: chartPalette.textMuted, fontWeight: 500, lineHeight: 1.2 }}>
                a 1ª avaliação
              </Typography>
            </Box>
          )}
        </Box>

        {/* Value row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography sx={{
              fontSize: '26px',
              fontWeight: 700,
              color: chartPalette.navy,
              lineHeight: 1,
            }}>
              {currentValue}%
            </Typography>
            <SeverityBadge severity={severity} />
          </Box>

          {variation !== null && (
            <Typography sx={{
              fontSize: '15px',
              fontWeight: 700,
              color: variationColor,
              lineHeight: 1,
            }}>
              {variationArrow}{variation}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Separator ── */}
      <Box sx={{ height: '1px', backgroundColor: chartPalette.gridLine, margin: '0 16px' }} />

      {/* ── Chart ── */}
      <Box sx={{ padding: '8px 4px 0 4px' }}>
        <Box sx={{ position: 'relative', height: '170px' }}>
          <ZoneOverlay zones={zones} yMin={yMin} yMax={yMax} />
          <DataPointLabels
            chartData={chartData}
            yMin={yMin}
            yMax={yMax}
            accentColor={accentColor}
            chartMargin={chartMargin}
            chartHeight={170}
          />
          <LineChart
            series={[{
              data: chartData,
              color: accentColor,
              showMark: true,
              area: true,
              curve: 'catmullRom',
              valueFormatter: (value) => `${value}%`,
            }]}
            legend={{ hidden: true }}
            xAxis={[{
              data: xLabels,
              scaleType: 'band',
            }]}
            yAxis={[{
              min: yMin,
              max: yMax,
              valueFormatter: (value) => `${value}%`,
            }]}
            height={170}
            margin={chartMargin}
            sx={{
              position: 'relative',
              zIndex: 1,
              '& .MuiLineElement-root': {
                strokeWidth: 3,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
              },
              '& .MuiAreaElement-root': {
                fillOpacity: 0.1,
              },
              '& .MuiMarkElement-root': {
                stroke: chartPalette.white,
                strokeWidth: 2,
                fill: accentColor,
                r: 4,
              },
              '& .MuiMarkElement-root:last-of-type': {
                r: 6,
                strokeWidth: 2.5,
                filter: `drop-shadow(0 0 4px ${accentColor}40)`,
              },
              '& .MuiChartsAxis-tickLabel': {
                fontSize: '9px !important',
                fill: `${chartPalette.textMuted} !important`,
                fontWeight: '500 !important',
              },
              '& .MuiChartsAxis-line': {
                stroke: `${chartPalette.gridLine} !important`,
                strokeWidth: '1 !important',
              },
              '& .MuiChartsAxis-tick': {
                stroke: 'transparent !important',
              },
              '& .MuiChartsGrid-line': {
                stroke: `${chartPalette.gridLine} !important`,
                strokeWidth: '0.5 !important',
                opacity: '0.5 !important',
              },
            }}
            grid={{ horizontal: true }}
          />
        </Box>

        {/* ── X-axis sub-labels ── */}
        {xSubLabels && xSubLabels.length > 0 && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '0 38px 10px 38px',
            marginTop: '-4px',
          }}>
            {xSubLabels.map((sub, i) => (
              <Typography
                key={i}
                sx={{
                  fontSize: '8px',
                  fontWeight: sub.isActive ? 600 : 500,
                  color: sub.isActive ? accentColor : chartPalette.textMuted,
                  textAlign: 'center',
                  flex: 1,
                }}
              >
                {sub.label}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// ─── Main Component ───────────────────────────────────────────────
const CranialAsymmetryChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  const sortedData = [...data].sort((a, b) =>
    dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf()
  );

  // Format dates
  const dates = sortedData.map(item => dayjs(item.createdAt).format('DD MMM'));

  // Build sub-labels
  const xSubLabels = sortedData.map((item, index) => ({
    label: index === sortedData.length - 1
      ? 'Atual'
      : `${index + 1}ª avaliação`,
    isActive: index === sortedData.length - 1,
  }));

  // ── AP/BP calculations ──
  const apBpValues = sortedData.map(item => {
    const normalizedDifference = item.bp / item.ap;
    return parseFloat((normalizedDifference * 100).toFixed(2));
  });

  const apBpCurrent = apBpValues[apBpValues.length - 1];
  const apBpFirst = apBpValues[0];
  const apBpDiff = apBpCurrent - apBpFirst;
  const apBpVariation = apBpValues.length > 1
    ? `${apBpDiff > 0 ? '+' : ''}${apBpDiff.toFixed(2)}%`
    : null;
  const apBpDirection = apBpDiff > 0 ? 'up' : apBpDiff < 0 ? 'down' : 'neutral';
  const apBpSeverity = getApBpSeverity(apBpCurrent);

  const apBpMin = Math.min(...apBpValues);
  const apBpMax = Math.max(...apBpValues);
  const apBpYAxisMin = Math.max(0, Math.floor(apBpMin) - 5);
  const apBpYAxisMax = Math.ceil(apBpMax) + 5;

  // ── Plagiocephaly calculations ──
  const plagioValues = sortedData.map(item => {
    const digMaior = item.dd > item.de ? item.dd : item.de;
    const digMenor = item.dd < item.de ? item.dd : item.de;
    const normalizedDifference = (digMaior - digMenor) / digMaior;
    return parseFloat((normalizedDifference * 100).toFixed(2));
  });

  const plagioCurrent = plagioValues[plagioValues.length - 1];
  const plagioFirst = plagioValues[0];
  const plagioDiff = plagioCurrent - plagioFirst;
  const plagioVariation = plagioValues.length > 1
    ? `${plagioDiff > 0 ? '+' : ''}${plagioDiff.toFixed(2)}%`
    : null;
  const plagioDirection = plagioDiff > 0 ? 'up' : plagioDiff < 0 ? 'down' : 'neutral';
  const plagioSeverity = getPlagioSeverity(plagioCurrent);

  const plagioMin = Math.min(...plagioValues);
  const plagioMax = Math.max(...plagioValues);
  const plagioYAxisMin = Math.max(0, Math.floor(plagioMin) - 2);
  const plagioYAxisMax = Math.ceil(plagioMax) + 2;

  return (
    <Box className={styles.containerChart} sx={{ marginBottom: '16px' }}>
      <Typography
        variant="subtitle2"
        sx={{
          color: chartPalette.textDark,
          marginBottom: '12px',
          fontWeight: 600,
          fontSize: '14px',
        }}
      >
        Evolução das Medições
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '16px' }}>
        <ChartCard
          icon={<ChartLineIcon color={chartPalette.blue} />}
          title="Escala AP/BP"
          currentValue={apBpCurrent}
          severity={apBpSeverity}
          variation={apBpVariation}
          variationDirection={apBpDirection}
          accentColor={chartPalette.blue}
          accentBg={chartPalette.blueBg}
          chartData={apBpValues}
          xLabels={dates}
          xSubLabels={xSubLabels}
          zones={apBpZones}
          yMin={apBpYAxisMin}
          yMax={apBpYAxisMax}
        />

        <ChartCard
          icon={<BrainIcon color={chartPalette.pink} />}
          title="Plagiocefalia (CVAI)"
          currentValue={plagioCurrent}
          severity={plagioSeverity}
          variation={plagioVariation}
          variationDirection={plagioDirection}
          accentColor={chartPalette.pink}
          accentBg={chartPalette.pinkBg}
          chartData={plagioValues}
          xLabels={dates}
          xSubLabels={xSubLabels}
          zones={plagiocephalyZones}
          yMin={plagioYAxisMin}
          yMax={plagioYAxisMax}
        />
      </Box>
    </Box>
  );
};

export default CranialAsymmetryChart;
